import {
    ColumnDef,
    SortingState,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { ReactNode, useState } from "react";

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

interface DataTableProps<TData, TValue> {
    columns: ColumnDef<TData, TValue>[];
    data: TData[];
    onRowClick?: (data: TData) => void;
    title?: string;
    addButton?: ReactNode;

    pagination?: {
        limit: number;
        offset: number;
        setLimit: (limit: number) => void;
        setOffset: (offset: number) => void;
        total?: number;
    };
}

export function DataTable<TData, TValue>({
    columns,
    data,
    onRowClick,
    title,
    addButton,
    pagination,
}: DataTableProps<TData, TValue>) {
    const [sorting, setSorting] = useState<SortingState>([]);

    const table = useReactTable({
        data,

        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: {
            sorting,
            pagination: {
                pageIndex: pagination ? Math.floor(pagination.offset / pagination.limit) : 0,
                pageSize: pagination ? pagination.limit : 20,
            },
        },
        manualPagination: true,
    });

    const totalPages =
        pagination && pagination.total ? Math.ceil(pagination.total / pagination.limit) : undefined;
    const currentPage = pagination ? Math.floor(pagination.offset / pagination.limit) + 1 : 1;

    return (
        <div className="rounded-md border">
            {title || addButton ? (
                <div className="flex items-center gap-12 border-b p-4">
                    <span className={"text-xl font-semibold"}>{title}</span>
                    {addButton}
                </div>
            ) : null}
            <Table>
                <TableHeader>
                    {table.getHeaderGroups().map(headerGroup => (
                        <TableRow key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                                return (
                                    <TableHead key={header.id}>
                                        {header.isPlaceholder
                                            ? null
                                            : flexRender(
                                                  header.column.columnDef.header,
                                                  header.getContext()
                                              )}
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableHeader>
                <TableBody>
                    {table.getRowModel().rows?.length ? (
                        table.getRowModel().rows.map(row => (
                            <TableRow
                                key={row.id}
                                data-state={row.getIsSelected() && "selected"}
                                className="hover:bg-brand-purple cursor-pointer hover:bg-opacity-20"
                                onClick={() => onRowClick?.(row.original)}
                            >
                                {row.getVisibleCells().map(cell => (
                                    <TableCell key={cell.id}>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    ) : (
                        <TableRow>
                            <TableCell colSpan={columns.length} className="h-24 text-center">
                                No results.
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
            {pagination && (
                <div className="flex items-center justify-between p-4">
                    <div>
                        <button
                            onClick={() =>
                                pagination.setOffset(
                                    Math.max(pagination.offset - pagination.limit, 0)
                                )
                            }
                            disabled={pagination.offset === 0}
                            className="mr-2 rounded bg-gray-200 px-4 py-2 disabled:opacity-50"
                        >
                            Previous
                        </button>
                        <button
                            onClick={() => {
                                if (pagination.total) {
                                    const newOffset = pagination.offset + pagination.limit;
                                    if (newOffset < pagination.total) {
                                        pagination.setOffset(newOffset);
                                    }
                                } else {
                                    pagination.setOffset(pagination.offset + pagination.limit);
                                }
                            }}
                            disabled={
                                pagination.total !== undefined
                                    ? pagination.offset + pagination.limit >= pagination.total
                                    : false
                            }
                            className="rounded bg-gray-200 px-4 py-2 disabled:opacity-50"
                        >
                            Next
                        </button>
                    </div>
                    {totalPages !== undefined && (
                        <div>
                            Page {currentPage} of {totalPages}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
